import { useEffect, useRef, useState } from "react";
import { Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import CTA from "@/components/atoms/CTA";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import WeightInput from "@/components/molecules/WeightInput";
import { useInspectionFormat } from "@/components/organisms/InspectionModal/useInspectionFormat";
import { FinalAmountAssessment, FinalAmountWarning } from "@/types";
import { isSameWeight, validateWeight, WEIGHT_ISSUE_KEYS, WEIGHT_UNIT_LABELS, WeightInputValue } from "@/utils/weight";
import { assessmentKey, AssessmentReason, getAssessmentLines } from "./assessmentLines";
import { useEscapeForThisDialog } from "./useEscapeForThisDialog";
import styles from "./styles.module.css";

export interface FinalAmountWarningModalProps {
  /** The warning to show. It stays set while the dialog fades out after closing. */
  warning: FinalAmountWarning | null;
  opened: boolean;
  /** Entry labels by assessmentKey. Missing ones read "Order {orderId}"; a single entry shows no label. */
  labels?: Record<string, string>;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** The dialog's weights by assessmentKey, sent when at least one differs from what was submitted. */
  onResubmit: (weights: Record<string, WeightInputValue>) => void;
}

const isFlagged = (assessment: FinalAmountAssessment) => assessment.rules.length > 0;

/**
 * The backend's final amount warning as a dialog. It words what the warning says and lets the user correct the
 * flagged weights; it never decides what is plausible. Confirming resends the warned request, while editing a
 * weight turns that into "Save", which sends the new weights for a fresh check.
 */
export default function FinalAmountWarningModal({
  warning,
  opened,
  labels,
  isPending,
  onCancel,
  onConfirm,
  onResubmit,
}: FinalAmountWarningModalProps) {
  const { t } = useTranslation();

  const flagged = warning?.assessments.filter(isFlagged) ?? [];
  // The backend assesses every weight it was sent, so this is how many were submitted, for "2 of 5".
  const total = warning?.assessments.length ?? 0;

  // Escape cancels this dialog alone, not the modal or drawer behind it, and does nothing while a request is in flight.
  useEscapeForThisDialog(opened, () => {
    if (!isPending) onCancel();
  });

  return (
    <Modal
      opened={opened}
      // A request is in flight, so closing now would leave the user without its answer.
      onClose={isPending ? () => undefined : onCancel}
      closeOnClickOutside={false}
      // Escape is handled by useEscapeForThisDialog, which keeps it from reaching the modals behind.
      closeOnEscape={false}
      showCloseButton
      size={560}
      data-testid="final-amount-warning"
      title={
        <span className={styles.title}>
          <SVGAsset name="warning-triangle" width="20" height="20" />
          <Text span size="xl">
            {total === 1
              ? t("orders:finalAmountWarning.titleSingle")
              : t("orders:finalAmountWarning.titleMany", { count: flagged.length, total })}
          </Text>
        </span>
      }
    >
      {warning && (
        <WarningBody
          key={warning.confirmationToken}
          flagged={flagged}
          others={total - flagged.length}
          single={total === 1}
          labels={labels}
          isPending={isPending}
          closing={!opened}
          onCancel={onCancel}
          onConfirm={onConfirm}
          onResubmit={onResubmit}
        />
      )}
    </Modal>
  );
}

interface WarningBodyProps extends Pick<FinalAmountWarningModalProps, "labels" | "isPending" | "onCancel"> {
  flagged: FinalAmountAssessment[];
  /** How many of the submitted weights the backend found fine. */
  others: number;
  single: boolean;
  /** The warning is gone and the dialog is fading out: what it shows can no longer be sent. */
  closing: boolean;
  onConfirm: () => void;
  onResubmit: FinalAmountWarningModalProps["onResubmit"];
}

/** The entries and the footer. Holds the edited weights, so it is keyed by the warning it shows. */
const WarningBody = ({
  flagged,
  others,
  single,
  labels,
  isPending,
  closing,
  onCancel,
  onConfirm,
  onResubmit,
}: WarningBodyProps) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, WeightInputValue>>(() =>
    Object.fromEntries(flagged.map((assessment) => [assessmentKey(assessment), { ...assessment.quantity }]))
  );

  const changed = flagged.some((assessment) => !isSameWeight(values[assessmentKey(assessment)], assessment.quantity));
  const invalid = flagged.some((assessment) => validateWeight(values[assessmentKey(assessment)]) !== null);

  // A new warning replaces the body, and with it the button that had the focus. Mantine's focus trap only moves the
  // focus when the dialog opens, so the first field takes it here instead of leaving it on the page.
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (document.activeElement === document.body) {
      body.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    }
  }, []);

  return (
    <>
      <Stack gap="md" className={styles.body} ref={body}>
        {others > 0 && (
          <Text size="sm" color="secondary">
            {t("orders:finalAmountWarning.othersFine", { count: others })}
          </Text>
        )}
        {flagged.map((assessment, index) => {
          const key = assessmentKey(assessment);
          return (
            <Entry
              key={key}
              assessmentKey={key}
              assessment={assessment}
              label={
                single
                  ? undefined
                  : (labels?.[key] ?? t("orders:finalAmountWarning.orderLabel", { orderId: assessment.orderId }))
              }
              value={values[key]}
              onChange={(value) => setValues((previous) => ({ ...previous, [key]: value }))}
              disabled={isPending || closing}
              autoFocus={index === 0}
            />
          );
        })}
      </Stack>
      <ModalCTAs>
        <CTA intent="tertiary" onClick={onCancel} disabled={isPending} data-testid="final-amount-warning-cancel">
          {t("actions:cancel")}
        </CTA>
        {changed ? (
          <CTA
            onClick={() => onResubmit(values)}
            disabled={isPending || closing || invalid}
            loading={isPending}
            data-testid="final-amount-warning-save"
          >
            {t("actions:save")}
          </CTA>
        ) : (
          <CTA
            intent="secondary"
            onClick={onConfirm}
            disabled={isPending || closing}
            loading={isPending}
            data-testid="final-amount-warning-confirm"
          >
            {single ? t("orders:finalAmountWarning.confirm") : t("orders:finalAmountWarning.confirmAll")}
          </CTA>
        )}
      </ModalCTAs>
    </>
  );
};

interface EntryProps {
  assessment: FinalAmountAssessment;
  assessmentKey: string;
  label?: string;
  value: WeightInputValue;
  onChange: (value: WeightInputValue) => void;
  disabled: boolean;
  autoFocus: boolean;
}

/** One flagged weight: what the backend found, why it looks off, and the field to correct it in. */
const Entry = ({ assessment, assessmentKey: key, label, value, onChange, disabled, autoFocus }: EntryProps) => {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const lines = getAssessmentLines(assessment);
  const { suggestion } = assessment;
  const issue = validateWeight(value);
  // The reasons and the settlement describe the submitted value, so they go once it is changed. Changing it back
  // brings them back.
  const edited = !isSameWeight(value, assessment.quantity);
  const field = useRef<HTMLDivElement>(null);

  const facts: { label: string; text: string }[] = [];
  if (lines.expected) {
    const amount = format.number(lines.expected.amountKg);
    facts.push({
      label: t("orders:finalAmountWarning.expected"),
      text:
        lines.expected.source === "ORDER_ESTIMATE"
          ? t("orders:finalAmountWarning.fromEstimate", { amount })
          : lines.expected.historySampleSize == null
            ? t("orders:finalAmountWarning.fromHistoryNoCount", { amount })
            : t("orders:finalAmountWarning.fromHistory", { amount, count: lines.expected.historySampleSize }),
    });
  }
  if (lines.maximum) {
    const amount = format.number(lines.maximum.amountKg);
    facts.push({
      label: t("orders:finalAmountWarning.maximum"),
      text:
        lines.maximum.source === "CONTRACT"
          ? t("orders:finalAmountWarning.maximumContract", { amount })
          : t("orders:finalAmountWarning.maximumFraction", { amount }),
    });
  }
  if (lines.settlement && !edited) {
    facts.push({
      label: t("orders:finalAmountWarning.settlement"),
      text: t("orders:finalAmountWarning.settlementAbout", {
        amount: format.amount(lines.settlement.amount, lines.settlement.currency),
      }),
    });
  }

  const reasonText = (reason: AssessmentReason) => {
    switch (reason.kind) {
      case "wrongUnit": {
        const factor = format.number(reason.factor);
        return reason.direction === "high"
          ? t("orders:finalAmountWarning.wrongUnitHigh", { factor })
          : t("orders:finalAmountWarning.wrongUnitLow", { factor });
      }
      case "deviation":
        return reason.direction === "high"
          ? t("orders:finalAmountWarning.farMore")
          : t("orders:finalAmountWarning.farLess");
      case "maximum":
        return t("orders:finalAmountWarning.aboveMaximum");
    }
  };

  return (
    <div className={styles.entry} data-edited={edited || undefined} data-testid={`final-amount-warning-entry-${key}`}>
      {label && (
        <Text strong size="sm">
          {label}
        </Text>
      )}
      {facts.length > 0 && (
        <div className={styles.facts}>
          {facts.map((fact) => (
            <div key={fact.label} className={styles.fact}>
              <Text size="sm" color="secondary">
                {fact.label}
              </Text>
              <Text size="sm">{fact.text}</Text>
            </div>
          ))}
        </div>
      )}
      {!edited &&
        lines.reasons.map((reason) => (
          <Text key={reason.kind} size="sm">
            {reasonText(reason)}
          </Text>
        ))}
      <div ref={field}>
        <WeightInput
          value={value}
          onChange={onChange}
          aria-label={label ?? t("orders:finalAmount")}
          error={issue && t(WEIGHT_ISSUE_KEYS[issue])}
          disabled={disabled}
          autoFocusInModal={autoFocus}
          data-testid={`final-amount-warning-weight-${key}`}
        />
      </div>
      {suggestion && !edited && (
        <div>
          <CTA
            intent="secondary"
            size="xs"
            onClick={() => {
              onChange({ ...suggestion.quantity });
              // The button goes now that the value is changed, so the focus moves to the field instead of the body.
              field.current?.querySelector("input")?.focus();
            }}
            disabled={disabled}
            data-testid={`final-amount-warning-suggestion-${key}`}
          >
            {t("orders:finalAmountWarning.didYouMean", {
              // Tonnes have up to 3 decimals, and the label must say exactly what the field is filled with.
              amount: format.number(suggestion.quantity.amount, { decimals: 3 }),
              unit: WEIGHT_UNIT_LABELS[suggestion.quantity.unit],
            })}
          </CTA>
        </div>
      )}
    </div>
  );
};
